import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Eye, Truck } from "lucide-react";
import { api } from "../../../lib/auth";

const STATUSES = ["all", "pending", "processing", "shipped", "delivered", "cancelled"];

const STATUS_COLORS = {
  pending: "bg-[#FFF5D6] text-amber-700",
  processing: "bg-[#EEF6FF] text-blue-700",
  shipped: "bg-violet-100 text-violet-700",
  delivered: "bg-[#E8F5E9] text-[#1FA868]",
  cancelled: "bg-rose-50 text-rose-600",
};

export default function StoreOrders() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await api.get("/t/store/orders", { params: { status: filter } });
    setRows(r.data);
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = rows.filter((o) =>
    !search ||
    o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  // 📊 Stats
  const total = rows.length;
  const delivered = rows.filter(o => o.status === "delivered").length;
  const revenue = rows.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="p-6 lg:p-8 space-y-6">

      {/* 🔥 Header */}
      <div>
        <h1 className="font-display font-extrabold text-3xl text-black">
          Online Orders
        </h1>
        <p className="text-black/60 text-sm mt-1">
          Manage and track all store orders.
        </p>
      </div>

      {/* 📊 Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#EEF6FF] p-5 rounded-2xl border border-black/5">
          <div className="text-xs text-black/50">Total Orders</div>
          <div className="text-2xl font-bold">{total}</div>
        </div>

        <div className="bg-[#E8F5E9] p-5 rounded-2xl border border-black/5">
          <div className="text-xs text-black/50">Delivered</div>
          <div className="text-2xl font-bold text-[#1FA868]">{delivered}</div>
        </div>

        <div className="bg-[#FFF5D6] p-5 rounded-2xl border border-black/5">
          <div className="text-xs text-black/50">Revenue</div>
          <div className="text-2xl font-bold">₦{revenue.toLocaleString()}</div>
        </div>
      </div>

      {/* 🔍 Search + Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 bg-white border border-black/10 px-3 py-2 rounded-xl flex-1 max-w-md">
          <Search size={16} className="text-black/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search orders..."
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${
                filter === s
                  ? "bg-[#1FA868] text-white"
                  : "bg-white border border-black/10 text-black/70"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* 📋 Table */}
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden shadow-sm">

        <div className="px-6 py-4 border-b border-black/8 flex justify-between">
          <h2 className="font-semibold text-black">Orders</h2>
          <span className="text-xs text-black/50">{filtered.length} records</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-black/55 text-[11px] uppercase">
              <tr>
                <th className="px-6 py-3 text-left">Order</th>
                <th className="px-6 py-3 text-left">Customer</th>
                <th className="px-6 py-3 text-left">Items</th>
                <th className="px-6 py-3 text-left">Total</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Date</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-black/5">
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-[#F9FAFB]">

                  <td className="px-6 py-4 font-mono text-xs">
                    {o.order_number}
                  </td>

                  <td className="px-6 py-4">
                    <div className="font-semibold">{o.customer_name}</div>
                    <div className="text-xs text-black/50">{o.customer_email}</div>
                  </td>

                  <td className="px-6 py-4 text-black/70">
                    {(o.items || []).length} items
                  </td>

                  <td className="px-6 py-4 font-bold">
                    ₦{Number(o.total || 0).toLocaleString()}
                  </td>

                  <td className="px-6 py-4">
                    <span className={`text-[10px] px-2 py-1 rounded-md font-bold ${STATUS_COLORS[o.status]}`}>
                      {o.status}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-xs text-black/50">
                    {(o.created_at || "").slice(0, 10)}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setSelected(o)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-black/10 hover:border-black/25"
                    >
                      <Eye size={12} /> View
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 🧾 Side Panel */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg bg-white h-full shadow-xl" onClick={(e) => e.stopPropagation()}>

            <div className="p-5 border-b border-black/8 flex justify-between">
              <h2 className="font-bold text-lg">{selected.order_number}</h2>
              <button onClick={() => setSelected(null)}>×</button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs text-black/50">Customer</div>
                <div className="font-semibold">{selected.customer_name}</div>
                <div className="text-xs text-black/50">{selected.customer_email}</div>
              </div>

              <div>
                <div className="text-xs text-black/50 mb-2">Items</div>
                {(selected.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm border-b py-1">
                    <span>{it.name} × {it.qty}</span>
                    <span>₦{(it.price * it.qty).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span>₦{selected.total?.toLocaleString()}</span>
              </div>

              <div>
                <div className="text-xs text-black/50 mb-2">Update status</div>
                <div className="grid grid-cols-2 gap-2">
                  {["processing", "shipped", "delivered", "cancelled"].map((st) => (
                    <button
                      key={st}
                      onClick={() => setStatus(selected.id, st)}
                      className={`py-2 rounded-lg text-xs font-semibold ${STATUS_COLORS[st]}`}
                    >
                      <Truck size={12} className="inline mr-1" /> {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}